import { useState } from "react";
import { useTranslation } from "react-i18next";
import { FileText, Files } from "lucide-react";
import { Button } from "components/common/Button";
import type { FileCategory } from "../types";
import type { UploadedFilesModalContentProps } from "./types";

interface CategorySection {
  category: FileCategory;
  titleKey: string;
}

const CATEGORY_SECTIONS: CategorySection[] = [
  { category: "floorPlans", titleKey: "smartSurveyor.floorPlans.title" },
  {
    category: "electricalDiagrams",
    titleKey: "smartSurveyor.electricalLineDiagrams.title",
  },
  {
    category: "billOfQuantities",
    titleKey: "smartSurveyor.billOfQuantities.title",
  },
  { category: "otherFiles", titleKey: "smartSurveyor.otherFiles.title" },
];

const resolveCategory = (category: string): FileCategory => {
  if (
    category === "floorPlans" ||
    category === "electricalDiagrams" ||
    category === "billOfQuantities"
  ) {
    return category;
  }
  return "otherFiles";
};

export const UploadedFilesModalContent = ({
  files,
  onClose,
}: UploadedFilesModalContentProps) => {
  const { t } = useTranslation();

  const [displayedFiles] = useState(files);

  return (
    <div className="flex flex-col">
      <div className="flex items-center gap-3 border-b border-gray-100 pb-4">
        <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-blue-50">
          <Files size={20} className="text-blue-600" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-gray-900">
            {t("smartSurveyor.uploadedFiles.title")}
          </h2>
          <p className="text-xs text-gray-500">
            {t("smartSurveyor.uploadedFiles.fileCount", {
              count: displayedFiles.length,
            })}
          </p>
        </div>
      </div>
      <div className="max-h-[55vh] space-y-6 overflow-y-auto py-4">
        {displayedFiles.length === 0 && (
          <div className="flex flex-col items-center py-10 text-center">
            <Files size={28} className="mb-2 text-gray-300" />
            <p className="text-sm text-gray-500">
              {t("smartSurveyor.uploadedFiles.empty")}
            </p>
          </div>
        )}
        {CATEGORY_SECTIONS.map(({ category, titleKey }) => {
          const categoryFiles = displayedFiles.filter(
            (file) => resolveCategory(file.category) === category,
          );
          if (categoryFiles.length === 0) return null;

          return (
            <div key={category}>
              <div className="mb-2 flex items-center gap-2">
                <span className="flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-blue-50 px-1.5 text-xs font-semibold text-blue-600">
                  {categoryFiles.length}
                </span>
                <h3 className="text-xs font-bold uppercase tracking-wider text-blue-700">
                  {t(titleKey)}
                </h3>
              </div>
              <ul className="space-y-1.5">
                {categoryFiles.map((file) => (
                  <li
                    key={file.id}
                    className="flex items-center gap-3 rounded-lg bg-gray-50 px-3 py-2.5 transition-colors hover:bg-blue-50"
                  >
                    <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md bg-white shadow-sm">
                      <FileText size={16} className="text-blue-600" />
                    </div>
                    <span className="truncate text-sm font-medium text-gray-700">
                      {file.originalFileName}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>
      <div className="flex justify-end border-t border-gray-100 pt-4">
        <Button size="lg" onClick={onClose}>
          {t("smartSurveyor.common.close")}
        </Button>
      </div>
    </div>
  );
};
